import { graphql } from '@/utils/graphql';
import { parseAwsJson } from './awsJson';

// AI Insights projects in DynamoDB (InsightProject, owner-only), per user and workspace. The repo
// in pages/Insights/data keeps the UI's interface over these calls.

export type InsightProjectRow = {
  id: string;
  ownerId: string;
  workspaceId: string;
  name: string;
  description?: string | null;
  instructions?: string | null;
  knowledge?: string | null;
  datasetIds?: string[] | null;
  shared?: boolean | null;
  ownerName?: string | null;
  lastActivityAt: string;
  createdAt?: string;
  updatedAt?: string;
};

export type InsightProjectFields = {
  name: string;
  description?: string | null;
  instructions?: string | null;
  knowledge?: string | null;
  datasetIds?: string[];
  shared?: boolean;
  ownerName?: string | null;
};

// Not in the typed command maps of @/api (hand-edited, not regenerated), as in embedToken.
const call = <T>(command: string, variables: Record<string, unknown>): Promise<T> =>
  graphql(command as unknown as never, variables as unknown as never) as unknown as Promise<T>;

const defined = (fields: Partial<InsightProjectFields>) =>
  Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined));

export default class InsightProjectStore {
  static async listForOwner(ownerId: string, workspaceId: string): Promise<InsightProjectRow[]> {
    // Pages one after another: each needs the previous one's token.
    const page = async (nextToken?: string | null): Promise<InsightProjectRow[]> => {
      const rs = await call<{ items: InsightProjectRow[]; nextToken?: string | null }>(
        'listInsightProjectsByOwner',
        {
          ownerId,
          workspaceId: { eq: workspaceId },
          limit: 100,
          nextToken,
        },
      );
      const items = (rs?.items ?? []).filter(Boolean);
      return rs?.nextToken ? items.concat(await page(rs.nextToken)) : items;
    };
    return page();
  }

  static async create(
    id: string,
    ownerId: string,
    workspaceId: string,
    fields: InsightProjectFields,
    lastActivityAt = Date.now(),
  ): Promise<InsightProjectRow> {
    return call('createInsightProject', {
      input: {
        id,
        ownerId,
        workspaceId,
        ...defined(fields),
        lastActivityAt: new Date(lastActivityAt).toISOString(),
      },
    });
  }

  static async update(
    id: string,
    fields: Partial<InsightProjectFields>,
  ): Promise<InsightProjectRow> {
    return call('updateInsightProject', {
      input: { id, ...defined(fields), lastActivityAt: new Date().toISOString() },
    });
  }

  static async remove(id: string): Promise<void> {
    await call('deleteInsightProject', { input: { id } });
  }

  /** Edit a project someone else shared with the workspace (its content only). */
  static async updateShared(
    id: string,
    fields: Partial<InsightProjectFields>,
  ): Promise<InsightProjectRow> {
    const { name, description, instructions, knowledge, datasetIds } = fields;
    const rs = await call<{ project: string }>('beemAgentUpdateSharedInsightProject', {
      query: defined({
        id,
        name,
        description,
        instructions,
        knowledge,
        datasetIds,
      } as Partial<InsightProjectFields>),
    });
    return parseAwsJson<InsightProjectRow>(rs.project);
  }
}
